import { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Scale, RefreshCw } from "lucide-react";

/**
 * CompareModePanel — side-by-side Paper (Aanar sim) vs Live (IBKR MCP) P&L.
 * Only shown for bots that have placed at least one trade so far.
 */
export default function CompareModePanel({ botId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bots/${botId}/compare`);
      setData(res.data);
    } catch {
      /* ignore — panel just stays empty */
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && !data) load();
    // eslint-disable-next-line
  }, [open]);

  const pill = (v) => (v >= 0 ? "gain" : "loss");
  const arrow = (v) => (v >= 0 ? "▲" : "▼");

  return (
    <Card className="p-3 rounded-xl border-border mt-3" data-testid="compare-panel">
      <button
        data-testid="compare-toggle"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-emerald-400" />
          <div className="text-xs font-semibold uppercase tracking-wider">Compare · paper vs live</div>
        </div>
        <div className="text-[11px] text-muted-foreground">{open ? "Hide" : "Show"}</div>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid="compare-body">
          {!data && loading && (
            <div className="text-[11px] text-muted-foreground italic">Comparing…</div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary/30 border border-border/50 p-2.5" data-testid="compare-sim">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Aanar · Sim</div>
                    <Badge className="border-0 text-[9px] font-mono-tab bg-emerald-500/15 text-emerald-400">PAPER</Badge>
                  </div>
                  <div className={`font-mono-tab text-lg font-semibold mt-1 ${pill(data.sim.total_pnl)}`}>
                    {arrow(data.sim.total_pnl)} {fmtMoney(Math.abs(data.sim.total_pnl))}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono-tab mt-0.5">
                    {data.sim.return_pct >= 0 ? "+" : ""}{data.sim.return_pct.toFixed(2)}% · {data.sim.trade_count} trades
                  </div>
                </div>

                <div className={`rounded-lg bg-secondary/30 border p-2.5 ${data.live.connected ? "border-border/50" : "border-dashed border-border/40"}`}
                     data-testid="compare-live">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase text-muted-foreground tracking-wider">IBKR · Live</div>
                    <Badge className={`border-0 text-[9px] font-mono-tab ${
                      data.live.connected ? "bg-amber-500/15 text-amber-400" : "bg-muted/30 text-muted-foreground"
                    }`}>
                      {data.live.connected ? "LIVE" : "OFFLINE"}
                    </Badge>
                  </div>
                  {data.live.connected ? (
                    <>
                      <div className={`font-mono-tab text-lg font-semibold mt-1 ${pill(data.live.total_pnl)}`}>
                        {arrow(data.live.total_pnl)} {fmtMoney(Math.abs(data.live.total_pnl))}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono-tab mt-0.5">
                        {data.live.return_pct >= 0 ? "+" : ""}{data.live.return_pct.toFixed(2)}% · {data.live.positions.length} positions
                      </div>
                    </>
                  ) : (
                    <div className="text-[10px] text-muted-foreground italic mt-1">
                      Connect IBKR in Settings to see live comparison.
                    </div>
                  )}
                </div>
              </div>

              {data.live.connected && data.diff_bps !== null && (
                <div className="rounded-lg bg-black/20 border border-border/40 p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Divergence</div>
                  <div className={`text-xs font-mono-tab mt-0.5 ${data.diff_bps > 0 ? "gain" : data.diff_bps < 0 ? "loss" : ""}`}>
                    {data.diff_bps > 0 ? `Sim outperformed live by ${data.diff_bps} bps` :
                     data.diff_bps < 0 ? `Live outperformed sim by ${Math.abs(data.diff_bps)} bps` :
                     "Perfectly matched"}
                  </div>
                </div>
              )}

              {data.sim.positions.length > 0 && (
                <div className="rounded-lg border border-border/40 overflow-hidden">
                  <div className="grid grid-cols-4 gap-1 px-2 py-1 text-[9px] uppercase text-muted-foreground bg-secondary/40 font-mono-tab">
                    <div>Symbol</div><div className="text-right">Sim P&L</div><div className="text-right">Live P&L</div><div className="text-right">Δ</div>
                  </div>
                  {data.sim.positions.map((s) => {
                    const live = data.live.positions.find((l) => l.symbol === s.symbol);
                    const livePnl = live ? live.unrealized_pnl + live.realized_pnl : null;
                    const simPnl = s.unrealized_pnl + s.realized_pnl;
                    const delta = livePnl !== null ? (simPnl - livePnl) : null;
                    return (
                      <div key={s.symbol} className="grid grid-cols-4 gap-1 px-2 py-1 text-[11px] border-t border-border/40 font-mono-tab"
                           data-testid={`compare-row-${s.symbol}`}>
                        <div className="font-semibold">{s.symbol}</div>
                        <div className={`text-right ${pill(simPnl)}`}>{fmtMoney(simPnl)}</div>
                        <div className={`text-right ${livePnl === null ? "text-muted-foreground italic" : pill(livePnl)}`}>
                          {livePnl === null ? "—" : fmtMoney(livePnl)}
                        </div>
                        <div className={`text-right ${delta === null ? "text-muted-foreground" : pill(delta)}`}>
                          {delta === null ? "—" : fmtMoney(delta)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button onClick={load} disabled={loading}
                      data-testid="compare-refresh"
                      className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh comparison
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
